import { promoSuppressionSelectors } from 'ducks/promoSuppression'
import { shouldSuppressE2eOverlays } from 'e2e/shouldSuppressE2eOverlays'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { useVisibleStructLogic } from 'persistentContent/utils/useVisibleStructLogic'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { ActionType, EventType } from 'types/tracking'

export const useToastNotificationLogic = () => {
  const suppressedPromoTypes = useSelector(promoSuppressionSelectors.getSuppressedPromoTypes)

  // This filter function is used to suppress toast notifications based on promo type suppression
  // If ANY promo type is suppressed, suppress ALL toast notifications during that period
  // This is particularly important for RegFlow 3.0 (MultiStepRegisterForm) where promos and
  // toast notifications are suppressed during Steps 2-4 (account creation, team setup, verification)
  // to prevent interruptions during the critical registration flow
  const toastNotificationFilter = useMemo(() => {
    return (struct: any) => {
      // If there are any active suppressors, hide toast notifications
      return suppressedPromoTypes.length === 0
    }
  }, [suppressedPromoTypes])

  const {
    open,
    setOpen,
    currentKey: currentNotifKey,
    setCurrentKey: setCurrentNotifKey,
    forceHide,
    setForceCheck,
    currentItem: currentNotif,
    trackAllDismissed,
  } = useVisibleStructLogic('toast-notification', toastNotificationFilter)

  const { trackEvent } = useTrackComponent({
    componentKey: 'toast_notification',
    eventName: 'User interacted with a toast notification',
  })

  const onCloseComplete = () => {
    if (open && forceHide) {
      // Don't clear if this was just the result of forceHide
      return
    }
    trackAllDismissed()
    setOpen(false)

    trackEvent(
      EventType.USER_INTERACTION,
      { type: ActionType.CLICK },
      {
        eventName: 'User dismissed a toast notification',
        notif_id: currentNotifKey,
      }
    )
    setCurrentNotifKey(undefined)
    setForceCheck((prev) => prev + 1)
  }

  const onNotifInteract = () => {
    trackEvent(
      EventType.USER_INTERACTION,
      { type: ActionType.CLICK },
      {
        eventName: 'User accepted a toast notification',
        notif_id: currentNotifKey,
      }
    )
  }

  return {
    open: open && (forceHide === undefined || !forceHide) && !shouldSuppressE2eOverlays(),
    onCloseComplete,
    onInteract: onNotifInteract,
    currentNotif,
    currentNotifKey,
  }
}
